import React from 'react';
import { ExternalLink, Shield, Microscope, Activity, Heart } from 'lucide-react';
import { AppShell } from '../components/Layout';

const references = [
  { title: 'Chan KS et al. Clinical validation of AI-enabled wound imaging (diabetic foot ulcers). Int Wound J. 2022.', url: 'https://onlinelibrary.wiley.com/doi/10.1111/iwj.13603' },
  { title: 'Fong KY et al. Clinical validation of smartphone app for VLU measurement. Int Wound J. 2023.', url: 'https://onlinelibrary.wiley.com/doi/10.1111/iwj.13918' },
  { title: 'FDA Clinical Decision Support Guidance. Sep 2022.', url: 'https://www.fda.gov/media/109618/download' },
];

const features = [
  { icon: Microscope, color: '#1B6CA8', bg: 'rgba(27,108,168,0.08)', title: 'Automated Wound Measurement', desc: 'Accurately captures wound length, width, and area using advanced AI image analysis.' },
  { icon: Activity, color: '#059669', bg: 'rgba(5,150,105,0.08)', title: 'Tissue Segmentation', desc: 'Differentiates wound bed into granulation, slough, and necrotic tissue to guide treatment.' },
  { icon: Shield, color: '#7C3AED', bg: 'rgba(124,58,237,0.08)', title: 'FDA-Aligned Design', desc: 'Designed under FDA CDS guidelines and SaMD / ISO/IEC standards.' },
  { icon: Heart, color: '#D97706', bg: 'rgba(217,119,6,0.08)', title: 'Telehealth Ready', desc: 'Supports remote wound monitoring through telehealth and home-care settings.' },
];

const ABOUT_STYLES = `
  .abt-wrap {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 640px;
    margin: 0 auto;
    padding-bottom: 32px;
    box-sizing: border-box;
    width: 100%;
    overflow: hidden;
  }

  /* Generic card */
  .abt-card {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 18px 16px;
    box-shadow: var(--shadow-sm);
  }

  .abt-section-title {
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--text-lo);
    margin-bottom: 12px;
  }

  .abt-body {
    font-size: 0.875rem;
    color: var(--text-mid);
    line-height: 1.75;
  }
  .abt-body + .abt-body { margin-top: 10px; }

  /* Features grid — 1 col on very small screens, 2 cols otherwise */
  .abt-features {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }
  @media (min-width: 360px) {
    .abt-features {
      grid-template-columns: 1fr 1fr;
    }
  }
  .abt-feature {
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 14px 12px;
  }
  .abt-feature-icon {
    width: 32px; height: 32px;
    border-radius: var(--radius-xs);
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 9px;
    flex-shrink: 0;
  }
  .abt-feature-title {
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--text-hi);
    margin-bottom: 4px;
    line-height: 1.3;
  }
  .abt-feature-desc {
    font-size: 0.75rem;
    color: var(--text-mid);
    line-height: 1.55;
  }

  /* Compliance — dark card */
  .abt-compliance {
    background: #0f172a;
    border: none;
    border-radius: var(--radius);
    padding: 18px 16px;
    box-shadow: var(--shadow-sm);
  }
  .abt-compliance-title {
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.35);
    margin-bottom: 12px;
  }
  .abt-compliance-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    min-height: 44px;
  }
  .abt-compliance-row:last-child { border-bottom: none; }
  .abt-compliance-text {
    font-size: 0.82rem;
    color: rgba(255,255,255,0.72);
  }

  /* Warning card */
  .abt-warning {
    background: rgba(217,119,6,0.07);
    border: 1px solid rgba(217,119,6,0.22);
    border-radius: var(--radius);
    padding: 16px;
  }
  .abt-warning-title {
    font-size: 0.875rem;
    font-weight: 700;
    color: #b45309;
    margin-bottom: 7px;
  }
  .abt-warning-body {
    font-size: 0.82rem;
    color: #92400e;
    line-height: 1.65;
  }

  /* Benefits list */
  .abt-benefit-row {
    display: flex;
    gap: 9px;
    font-size: 0.82rem;
    color: var(--text-mid);
    line-height: 1.5;
    margin-bottom: 8px;
    min-height: 36px;
    align-items: flex-start;
  }
  .abt-benefit-row:last-child { margin-bottom: 0; }
  .abt-check { color: var(--cyan); font-weight: 700; flex-shrink: 0; margin-top: 1px; }

  /* References */
  .abt-ref {
    display: flex;
    gap: 11px;
    padding: 12px;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius-xs);
    margin-bottom: 8px;
  }
  .abt-ref:last-child { margin-bottom: 0; }
  .abt-ref-num {
    font-family: 'DM Mono', monospace;
    font-weight: 700;
    color: var(--blue);
    font-size: 0.78rem;
    flex-shrink: 0;
    padding-top: 1px;
  }
  .abt-ref-title {
    font-size: 0.8rem;
    color: var(--text-mid);
    line-height: 1.6;
    margin-bottom: 6px;
    word-break: break-word;
  }
  .abt-ref-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.76rem;
    color: var(--blue);
    text-decoration: none;
    font-weight: 500;
    min-height: 44px;
    padding: 4px 0;
  }
  .abt-ref-link:hover { text-decoration: underline; }
`;

function useInjectStyles() {
  React.useEffect(() => {
    const id = 'cx-about-styles';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = ABOUT_STYLES;
    document.head.appendChild(el);
  }, []);
}

function useIsNarrow(breakpoint = 400) {
  const [narrow, setNarrow] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : true
  );
  React.useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e) => setNarrow(e.matches);
    mq.addEventListener('change', handler);
    setNarrow(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return narrow;
}

export default function AboutScreen() {
  useInjectStyles();
  const narrow = useIsNarrow(420);

  return (
    <AppShell title="About">
      <div className="abt-wrap">

        {/* Hero */}
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '16px',
          boxShadow: 'var(--shadow-sm)',
          display: 'flex',
          flexDirection: narrow ? 'column' : 'row',
          alignItems: narrow ? 'flex-start' : 'center',
          gap: 12,
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'hidden',
        }}>
          <img
            src="/logo.png"
            alt="Cytometrix"
            style={{
              height: 64,
              width: 'auto',
              maxWidth: 180,
              objectFit: 'contain',
              flexShrink: 0,
              display: 'block',
            }}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: '0.95rem',
              fontWeight: 700,
              color: 'var(--text-hi)',
              marginBottom: 3,
              lineHeight: 1.3,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}>
              Cytometrix Wound Analysis
            </div>
            <div style={{
              fontSize: '0.72rem',
              color: 'var(--text-lo)',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}>
              Version 0.1 · Web Edition
            </div>
          </div>
        </div>

        {/* About text */}
        <div className="abt-card">
          <div className="abt-section-title">About</div>
          <p className="abt-body">
            Cytometrix is a medical-grade wound analysis platform designed for clinicians and home-care providers. Utilizing AI-driven imaging, it delivers real-time wound measurement, tissue classification, and healing tracking.
          </p>
          <p className="abt-body">
            It bridges clinical precision with digital accessibility to streamline wound care workflows and enhance patient outcomes.
          </p>
        </div>

        {/* Features */}
        <div className="abt-card">
          <div className="abt-section-title">Key Features</div>
          <div className="abt-features">
            {features.map(({ icon: Icon, color, bg, title, desc }) => (
              <div key={title} className="abt-feature">
                <div className="abt-feature-icon" style={{ background: bg }}>
                  <Icon size={16} color={color} />
                </div>
                <div className="abt-feature-title">{title}</div>
                <div className="abt-feature-desc">{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance */}
        <div className="abt-compliance">
          <div className="abt-compliance-title">Compliance &amp; Standards</div>
          {['FDA CDS Guidelines (Sep 2022)', 'ISO/IEC SaMD Standards', 'HIPAA-Ready Architecture', 'Clinically Validated AI'].map(item => (
            <div key={item} className="abt-compliance-row">
              <Shield size={12} color="#3FA9F5" style={{ flexShrink: 0 }} />
              <span className="abt-compliance-text">{item}</span>
            </div>
          ))}
        </div>

        {/* Warning */}
        <div className="abt-warning">
          <div className="abt-warning-title">⚠ Important Notice</div>
          <p className="abt-warning-body">
            This application is a clinical adjunct — not a standalone diagnostic tool. It is designed to enhance, not replace, clinical expertise and professional judgment.
          </p>
        </div>

        {/* Clinical benefits */}
        <div className="abt-card">
          <div className="abt-section-title">Clinical Benefits</div>
          {[
            'Streamlines wound documentation with high accuracy',
            'Enables objective tracking via PAR and PVR metrics',
            'Reduces inter-rater variability',
            'Supports telehealth workflows',
          ].map(b => (
            <div key={b} className="abt-benefit-row">
              <span className="abt-check">✓</span>{b}
            </div>
          ))}
        </div>

        {/* References */}
        <div className="abt-card">
          <div className="abt-section-title">Scientific References</div>
          {references.map((ref, i) => (
            <div key={i} className="abt-ref">
              <span className="abt-ref-num">[{i + 1}]</span>
              <div>
                <p className="abt-ref-title">{ref.title}</p>
                <a href={ref.url} target="_blank" rel="noopener noreferrer" className="abt-ref-link">
                  View Source <ExternalLink size={11} />
                </a>
              </div>
            </div>
          ))}
        </div>

      </div>
    </AppShell>
  );
}
